import React from "react";
import { useTranslate } from "@refinedev/core";
import { useForm } from "@refinedev/antd";
import { Card, Form, Button, message, Spin, theme, Divider, Flex } from "antd";
import { ITenant } from "../../models/tenant";
import { TENANT_SETTINGS_KEY } from "../../authProvider";
import { usePageStyles } from "../../shared/styles";
import {
  TenantAutomationFields,
  TenantApiKeyFields,
  TenantAnnouncementFields,
} from "./TenantGeneralFields";

/**
 * Tenant-admin view of the tenant record ("my tenant").
 *
 * The superadmin editor renders the same underlying fields via
 * `TenantAutomationFields` / `TenantApiKeyFields` in their compact variant —
 * see `pages/tenants/components/TenantGeneralTab.tsx`.
 */
export const TenantGeneralForm: React.FC<{ scope: "self" }> = () => {
  const _t = useTranslate();
  const { formProps, saveButtonProps, formLoading, query } = useForm({
    action: "edit",
    resource: "tenants",
    id: "my-tenant",
    redirect: false,
    onMutationSuccess: (data) => {
      message.success(_t("settings.generalSettingsSaveSuccess"));
      const updated = data?.data as ITenant | undefined;
      if (updated?.settings) {
        localStorage.setItem(
          TENANT_SETTINGS_KEY,
          JSON.stringify(updated.settings),
        );
        window.dispatchEvent(new Event("tenantSettingsUpdated"));
      }
    },
    onMutationError: () => {
      message.error(_t("settings.generalSettingsSaveError"));
    },
  });
  const pageStyles = usePageStyles();
  const { token } = theme.useToken();

  if (formLoading) {
    return (
      <Card className="shadow-lg" style={pageStyles.stepperCard}>
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!query?.data?.data) {
    return (
      <Card className="shadow-lg" style={pageStyles.stepperCard}>
        <p style={{ color: token.colorTextSecondary }}>
          {_t("settings.generalSettingsNoTenant")}
        </p>
      </Card>
    );
  }

  return (
    <Form {...formProps} layout="vertical" className="general-settings-form">
      {/* The old page split these into two inner tabs, but they share one
          useForm and one save button, so the tabs bought nothing. */}
      <Card
        className="shadow-lg [&_.ant-card-body]:p-0"
        style={pageStyles.stepperCard}
      >
        <div className="pt-8 pb-4 px-8">
          <TenantAutomationFields variant="rich" />
        </div>
        <Divider className="my-0" />
        <div className="p-8">
          <TenantApiKeyFields variant="rich" />
        </div>
        <Divider className="my-0" />
        <div className="p-8">
          <TenantAnnouncementFields />
        </div>
      </Card>

      <Flex className="bottom-3 z-30 mt-2 py-3 rounded-xl justify-end" gap={12}>
        <Button
          type="primary"
          className="flex-1 !h-11 text-base font-semibold !rounded-lg"
          {...saveButtonProps}
        >
          {_t("buttons.save")}
        </Button>
      </Flex>
    </Form>
  );
};
